import { motion } from "framer-motion";

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

function LogoMark() {
  return (
    <div className="space-y-1.5">
      <div className="h-2 w-9 rotate-[28deg] rounded-full bg-white" />
      <div className="h-2 w-7 rotate-[28deg] rounded-full bg-white" />
      <div className="h-2 w-9 rotate-[28deg] rounded-full bg-white" />
    </div>
  );
}

function Metric({ value, label }) {
  return (
    <motion.div variants={fade} className="space-y-2">
      <p className="text-5xl font-semibold tracking-tight md:text-7xl">
        {value}
      </p>
      <p className="max-w-[12rem] text-sm uppercase tracking-[0.28em] text-white/45">
        {label}
      </p>
    </motion.div>
  );
}

function SectionLabel({ children }) {
  return (
    <p className="text-[11px] uppercase tracking-[0.38em] text-white/40">
      {children}
    </p>
  );
}

function MockDevice() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.96 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.8 }}
      className="relative mx-auto mt-20 max-w-6xl"
    >
      <div className="absolute inset-x-16 top-8 -z-10 h-56 rounded-full bg-violet-500/10 blur-3xl" />
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="rounded-[2.4rem] border border-white/10 bg-[#0a0a0a] p-4 shadow-[0_40px_140px_rgba(0,0,0,0.7)]"
      >
        <div className="rounded-[2rem] border border-white/10 bg-black p-6 md:p-8">
          <div className="flex items-center justify-between border-b border-white/10 pb-5">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
                <LogoMark />
              </div>
              <div>
                <p className="text-lg font-semibold tracking-[0.34em] md:text-xl">
                  ELEVATE
                </p>
                <p className="text-[10px] lowercase tracking-[0.4em] text-white/45 md:text-xs">
                  digital
                </p>
              </div>
            </div>
            <div className="hidden items-center gap-6 text-xs uppercase tracking-[0.28em] text-white/35 md:flex">
              <span>Services</span>
              <span>Results</span>
              <span>Contact</span>
            </div>
          </div>

          <div className="grid gap-10 pt-10 lg:grid-cols-[1.08fr_0.92fr] lg:items-center">
            <div>
              <SectionLabel>Performance Marketing</SectionLabel>
              <h3 className="mt-5 text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
                Scale beyond
                <span className="block text-white/60">limits.</span>
              </h3>
              <p className="mt-5 max-w-xl text-base leading-8 text-white/58 md:text-lg">
                A clearer system for brands that want more clients, stronger
                conversion, and scalable growth.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="https://calendly.com/elevate-escala/30min"
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full bg-white px-5 py-2.5 text-sm font-medium text-black transition hover:scale-[1.02]"
                >
                  Get More Clients
                </a>
                <a
                  href="#results"
                  className="rounded-full border border-white/15 px-6 py-3 text-center text-sm font-medium text-white transition hover:bg-white/[0.03]"
                >
                  View Results
                </a>
              </div>
            </div>

            <div className="rounded-[1.8rem] border border-white/10 bg-white/[0.025] p-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-[1.4rem] border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-xs uppercase tracking-[0.28em] text-white/38">
                    Monthly Leads
                  </p>
                  <p className="mt-3 text-4xl font-semibold tracking-tight">
                    47
                  </p>
                  <div className="mt-5 flex h-24 items-end gap-2">
                    {[24, 31, 43, 58, 66, 76].map((h, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${h}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: i * 0.06 }}
                        className="flex-1 rounded-t-lg bg-white"
                      />
                    ))}
                  </div>
                </div>
                <div className="rounded-[1.4rem] border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-xs uppercase tracking-[0.28em] text-white/38">
                    ROAS
                  </p>
                  <p className="mt-3 text-4xl font-semibold tracking-tight">
                    3.8x
                  </p>
                  <div className="mt-5 space-y-3">
                    <div className="h-2 rounded-full bg-white/10">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: "80%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9 }}
                        className="h-2 rounded-full bg-white"
                      />
                    </div>
                    <div className="h-2 rounded-full bg-white/10">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: "68%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.08 }}
                        className="h-2 rounded-full bg-violet-400/80"
                      />
                    </div>
                    <div className="h-2 rounded-full bg-white/10">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: "92%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.16 }}
                        className="h-2 rounded-full bg-white/70"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                {[
                  ["Ads", "Acquire demand"],
                  ["Funnels", "Convert attention"],
                  ["Systems", "Scale clearly"],
                ].map(([title, text]) => (
                  <div
                    key={title}
                    className="rounded-[1.3rem] border border-white/10 bg-white/[0.02] p-4"
                  >
                    <p className="text-base font-medium">{title}</p>
                    <p className="mt-2 text-sm leading-6 text-white/50">
                      {text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function ElevateDigitalAppleV2() {
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/80 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
              <LogoMark />
            </div>
            <div>
              <p className="text-xl font-semibold tracking-[0.34em] md:text-2xl">
                ELEVATE
              </p>
              <p className="text-[10px] lowercase tracking-[0.42em] text-white/45 md:text-sm">
                digital
              </p>
            </div>
          </div>

          <nav className="hidden items-center gap-8 text-xs uppercase tracking-[0.28em] text-white/45 md:flex">
            <a href="#services" className="transition hover:text-white">
              Services
            </a>
            <a href="#results" className="transition hover:text-white">
              Results
            </a>
            <a href="#contact" className="transition hover:text-white">
              Contact
            </a>
          </nav>

          <a
            href="https://calendly.com/elevate-escala/30min"
            target="_blank"
            rel="noreferrer"
            className="rounded-full bg-white px-7 py-3.5 text-center text-sm font-medium text-black transition hover:scale-[1.02]"
          >
            Get More Clients
          </a>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_center,rgba(255,255,255,0.08),transparent_24%),radial-gradient(circle_at_center,rgba(168,85,247,0.08),transparent_30%)]" />
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="mx-auto max-w-7xl px-6 pb-10 pt-20 lg:px-8 lg:pt-28"
          >
            <div className="max-w-4xl">
              <motion.p
                variants={fade}
                className="text-[11px] uppercase tracking-[0.4em] text-white/38"
              >
                Client Acquisition Systems
              </motion.p>
              <motion.h1
                variants={fade}
                className="mt-6 text-6xl font-semibold leading-[0.95] tracking-tight md:text-8xl lg:text-[7rem]"
              >
                Scale beyond
                <span className="block text-white/58">limits.</span>
              </motion.h1>
              <motion.p
                variants={fade}
                className="mt-8 max-w-2xl text-lg leading-8 text-white/58 md:text-xl"
              >
                Turn attention into clients with a cleaner acquisition system
                built to scale.
              </motion.p>
              <motion.div
                variants={fade}
                className="mt-10 flex flex-col gap-4 sm:flex-row"
              >
                <a
                  href="#contact"
                  className="rounded-full bg-white px-7 py-3.5 text-center text-sm font-medium text-black transition hover:scale-[1.02]"
                >
                  Get More Clients
                </a>
                <a
                  href="#results"
                  className="rounded-full border border-white/15 px-7 py-3.5 text-center text-sm font-medium text-white transition hover:bg-white/[0.03]"
                >
                  See Proof
                </a>
              </motion.div>
            </div>

            <MockDevice />
          </motion.div>
        </section>

        <section
          id="services"
          className="mx-auto max-w-7xl px-6 py-24 lg:px-8 md:py-32"
        >
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              variants={stagger}
            >
              <motion.div variants={fade}>
                <SectionLabel>Services</SectionLabel>
              </motion.div>
              <motion.h2
                variants={fade}
                className="mt-6 text-4xl font-semibold leading-tight tracking-tight md:text-6xl"
              >
                More clients.
                <span className="block text-white/58">Less guesswork.</span>
              </motion.h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              variants={stagger}
              className="space-y-10"
            >
              {[
                [
                  "Paid Ads",
                  "Attract qualified demand with campaigns built to generate real leads, not empty clicks.",
                ],
                [
                  "Funnels",
                  "Turn interest into booked calls with landing pages and messaging designed to convert.",
                ],
                [
                  "Systems",
                  "Scale with follow-up, tracking, and automation that keep growth organized and profitable.",
                ],
              ].map(([title, text]) => (
                <motion.div
                  key={title}
                  variants={fade}
                  className="border-b border-white/10 pb-8"
                >
                  <p className="text-3xl font-medium tracking-tight md:text-4xl">
                    {title}
                  </p>
                  <p className="mt-4 max-w-xl text-base leading-8 text-white/55 md:text-lg">
                    {text}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section
          id="results"
          className="border-y border-white/10 bg-white/[0.015]"
        >
          <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 md:py-32">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              variants={stagger}
              className="max-w-3xl"
            >
              <motion.div variants={fade}>
                <SectionLabel>Results</SectionLabel>
              </motion.div>
              <motion.h2
                variants={fade}
                className="mt-6 text-4xl font-semibold leading-tight tracking-tight md:text-6xl"
              >
                Proof that moves decisions.
              </motion.h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              variants={stagger}
              className="mt-16 grid gap-12 md:grid-cols-3"
            >
              <Metric value="+43" label="Leads in 21 days" />
              <Metric value="3.4x" label="Average ROAS" />
              <Metric value="14" label="Days to launch" />
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8 md:py-32">
          <div className="grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-20 lg:items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              variants={stagger}
            >
              <motion.div variants={fade}>
                <SectionLabel>Case Study</SectionLabel>
              </motion.div>
              <motion.h2
                variants={fade}
                className="mt-6 text-4xl font-semibold leading-tight tracking-tight md:text-6xl"
              >
                From scattered attention to clear demand.
              </motion.h2>
              <motion.p
                variants={fade}
                className="mt-6 max-w-xl text-lg leading-8 text-white/58"
              >
                We rebuilt the offer, simplified the conversion path, and
                launched a lean acquisition system that produced qualified leads
                fast.
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
              className="rounded-[2rem] border border-white/10 bg-white/[0.02] p-8 md:p-10"
            >
              <div className="space-y-6">
                {[
                  [
                    "Before",
                    "Unstable inquiries, weak follow-up, low visibility.",
                  ],
                  [
                    "After",
                    "Consistent lead flow, stronger conversion, better control.",
                  ],
                  ["System", "Ads, landing page, follow-up, and tracking."],
                ].map(([title, value]) => (
                  <div
                    key={title}
                    className="flex items-start justify-between gap-6 border-b border-white/10 pb-5 last:border-b-0"
                  >
                    <p className="text-sm uppercase tracking-[0.28em] text-white/38">
                      {title}
                    </p>
                    <p className="max-w-[70%] text-right text-base leading-7 text-white/80">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-10 rounded-[1.5rem] border border-white/10 bg-black p-5">
                <p className="text-xs uppercase tracking-[0.3em] text-white/35">
                  Revenue Visibility
                </p>
                <div className="mt-5 flex h-40 items-end gap-3">
                  {[18, 24, 32, 48, 56, 72, 90].map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${h}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: i * 0.05 }}
                      className={`flex-1 rounded-t-xl ${
                        i === 6 ? "bg-violet-400" : "bg-white"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section id="contact" className="pb-24 md:pb-32">
          <div className="mx-auto max-w-5xl px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
              className="rounded-[2.4rem] border border-white/10 bg-white/[0.02] p-10 text-center md:p-16"
            >
              <SectionLabel>Contact</SectionLabel>
              <h2 className="mt-6 text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
                Ready for a better client system?
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-white/58">
                Book a call and we’ll show you where leads are leaking, what to
                fix, and how to scale with more control.
              </p>
              <div className="mx-auto mt-10 flex max-w-2xl flex-col gap-4 md:flex-row">
                <input
                  type="email"
                  placeholder="Enter your work email"
                  className="flex-1 rounded-full border border-white/10 bg-black px-6 py-4 text-white placeholder:text-white/30 outline-none"
                />
                <a
                  href="https://calendly.com/elevate-escala/30min"
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full bg-white px-8 py-4 text-center text-sm font-medium text-black transition hover:scale-[1.02]"
                >
                  Get More Clients
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8 text-sm text-white/42 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
              <LogoMark />
            </div>
            <span className="tracking-[0.28em]">ELEVATE DIGITAL</span>
          </div>
          <p> Get more clients. Build a system. </p>
        </div>
      </footer>
    </div>
  );
}
